import React from 'react';
import { Critical, High, InfoGreen as Info, Low, Medium } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { abbreviateNumber } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { AvTooltip } from '../AvTooltip';

export enum SeverityLabels {
  Critical = 'CRITICAL',
  High = 'HIGH',
  Medium = 'MEDIUM',
  Low = 'LOW',
  Info = 'INFO',
  None = 'NONE',
}
export const SeverityLabelsWithoutEmpty = Object.fromEntries(
  Object.entries(SeverityLabels).filter(([, v]) => v !== SeverityLabels.Info && v !== SeverityLabels.None)
) as { [K in Exclude<keyof typeof SeverityLabels, 'Info' | 'None'>]: (typeof SeverityLabels)[K] };

export const SeverityPrimaryColors = {
  [SeverityLabels.Critical]: 'var(--dataviz-color-severity-primary-critical)',
  [SeverityLabels.High]: 'var(--dataviz-color-severity-primary-high)',
  [SeverityLabels.Medium]: 'var(--dataviz-color-severity-primary-medium)',
  [SeverityLabels.Low]: 'var(--dataviz-color-severity-primary-low)',
  [SeverityLabels.Info]: 'var(--dataviz-color-severity-primary-lowest)',
  [SeverityLabels.None]: 'var(--dataviz-color-severity-primary-lowest)',
};
export const SeverityBGColors = {
  [SeverityLabels.Critical]: 'var(--color-surface-severity-critical-primary)',
  [SeverityLabels.High]: 'var(--color-surface-severity-high-primary)',
  [SeverityLabels.Medium]: 'var(--color-surface-severity-medium-primary)',
  [SeverityLabels.Low]: 'var(--color-surface-severity-low-primary)',
  [SeverityLabels.Info]: 'var(--color-surface-severity-none-primary)',
  [SeverityLabels.None]: 'var(--color-surface-severity-none-primary)',
};
export const SeveritySecondaryBGColors = {
  [SeverityLabels.Critical]: 'var(--color-surface-severity-critical-secondary)',
  [SeverityLabels.High]: 'var(--color-surface-severity-high-secondary)',
  [SeverityLabels.Medium]: 'var(--color-surface-severity-medium-secondary)',
  [SeverityLabels.Low]: 'var(--color-surface-severity-low-secondary)',
};

export const SeverityIcons = {
  [SeverityLabels.None]: { icon: <Info style={{ color: SeverityPrimaryColors[SeverityLabels.None] }} />, order: 0 },
  [SeverityLabels.Info]: { icon: <Info style={{ color: SeverityPrimaryColors[SeverityLabels.Info] }} />, order: 1 },
  [SeverityLabels.Low]: { icon: <Low style={{ color: SeverityPrimaryColors[SeverityLabels.Low] }} />, order: 2 },
  [SeverityLabels.Medium]: { icon: <Medium style={{ color: SeverityPrimaryColors[SeverityLabels.Medium] }} />, order: 3 },
  [SeverityLabels.High]: { icon: <High style={{ color: SeverityPrimaryColors[SeverityLabels.High] }} />, order: 4 },
  [SeverityLabels.Critical]: { icon: <Critical style={{ color: 'var(--color-content-severity-critical-secondary)' }} />, order: 5 },
};

export const SEVERITY_LABELS = {
  INFO: 'Info',
  NONE: 'Info',
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};

export const severityOptions = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map(type => ({
  title: SEVERITY_LABELS[type],
  value: type,
}));
export const severityWithNone = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE'];
export const severityOptionsWithNone = severityWithNone.map(type => ({
  title: SEVERITY_LABELS[type],
  value: type,
}));

interface VulCountBySeverityProps {
  data: any;
  totals?: any;
  includeNone?: boolean;
}

export const VulCountBySeverity: React.FC<VulCountBySeverityProps> = ({ data, totals = {}, includeNone = true }) => {
  const severities = [
    SeverityLabels.Critical,
    SeverityLabels.High,
    SeverityLabels.Medium,
    SeverityLabels.Low,
    ...(includeNone ? [SeverityLabels.None] : []),
  ];

  return (
    <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
      {severities.map(severity => (
        <SeverityFormatter key={`tile_${severity}`} severity={severity} totals={totals} value={data?.[severity]} />
      ))}
    </Box>
  );
};

export const SeverityFormatter = ({ severity, value, totals }: { severity: SeverityLabels; value: number; totals?: any }) => {
  const hasColor = value > 0;
  const isAllSolved = value === totals?.[severity];
  return (
    <Box
      sx={{
        ...flex.row,
        ...(hasColor ? severitiesStyle[severity].wrapper : severitiesStyle.disabled.wrapper),
        borderRadius: 2,
        textAlign: 'end',
        width: 60,
      }}>
      <Box sx={{ ...severitiesStyle.label, ...(hasColor ? severitiesStyle[severity].label : severitiesStyle.disabled.label) }}>
        {severity[0]}
      </Box>
      <Box
        sx={{
          width: 36,
          pl: 0.5,
          pr: 1,
          ...flex.itemsCenter,
          whiteSpace: 'nowrap',
          fontWeight: hasColor && isAllSolved ? 600 : 400,
          textAlign: 'end',
        }}>
        <AvTooltip title={value} sx={{ width: '100%', textAlign: 'end' }}>
          <Box component="span" sx={{ width: '100%', textAlign: 'end', fontSize: 12 }}>
            {abbreviateNumber(value || 0)}
          </Box>
        </AvTooltip>
      </Box>
    </Box>
  );
};

export const severityBackgroundColor = (severity: SeverityLabels | string) =>
  SeverityPrimaryColors[severity] || 'var(--color-content-interactive-primary-default)';

export const severitiesStyle = {
  label: {
    ...flex.center,
    borderRadius: '6px',
    fontWeight: 600,
    height: 20,
    width: 20,
    color: 'var(--color-content-immutable-white)',
  },
  [SeverityLabels.Critical]: {
    wrapper: {
      background: SeverityBGColors[SeverityLabels.Critical],
    },
    label: {
      background: SeverityPrimaryColors[SeverityLabels.Critical],
    },
  },
  [SeverityLabels.High]: {
    wrapper: {
      background: SeverityBGColors[SeverityLabels.High],
    },
    label: {
      background: SeverityPrimaryColors[SeverityLabels.High],
    },
  },
  [SeverityLabels.Medium]: {
    wrapper: {
      background: SeverityBGColors[SeverityLabels.Medium],
    },
    label: {
      background: SeverityPrimaryColors[SeverityLabels.Medium],
    },
  },
  [SeverityLabels.Low]: {
    wrapper: {
      background: SeverityBGColors[SeverityLabels.Low],
    },
    label: {
      background: SeverityPrimaryColors[SeverityLabels.Low],
    },
  },
  [SeverityLabels.None]: {
    wrapper: {
      background: 'var(--color-surface-accent-teal-primary)',
    },
    label: {
      background: SeverityPrimaryColors[SeverityLabels.None],
    },
  },
  disabled: {
    wrapper: {
      background: SeverityBGColors[SeverityLabels.None],
    },
    label: {
      background: 'var(--color-content-severity-none-secondary)',
    },
  },
};

export const isValidSeverity = severity => Object.values(SeverityLabels).includes(severity);

export const getSeverityByScore = (score: number) => {
  if ((score >= 0 && score <= 25) || !score) {
    return SeverityPrimaryColors[SeverityLabels.Low];
  }
  if (score > 25 && score <= 50) {
    return SeverityPrimaryColors[SeverityLabels.Medium];
  }
  if (score > 50 && score <= 75) {
    return SeverityPrimaryColors[SeverityLabels.High];
  }
  if (score > 75) {
    return SeverityPrimaryColors[SeverityLabels.Critical];
  }
  return SeverityPrimaryColors[SeverityLabels.None];
};
